import {NySelectInput} from 'nyse-web-tools-common/lib/react';
import React, {FunctionComponent, useEffect} from 'react';
import {useDispatch} from 'react-redux';

import useStore from '../../hooks/useStore.hook';
import {getMMTypes} from '../../stores/mmTypes/mmTypes.actions';

export type MMTypesSelectProps = {
    disabled?: boolean;
    isMulti?: boolean;
    required?: boolean;
    /**
     * Which field of each /api/mm-types option to display. Defaults to `label` (the short,
     * MIC-scoped description — `MM`, `LMM`, …) so existing consumers (Liquidity Metrics) are
     * unchanged. MM Appointments passes `gboLabel` so the dropdown shows GBO's verbose display
     * string (`ARCA Options MM`, …) and matches its grid's MM Type column. Falls back to `label`
     * for any option whose `gboLabel` is absent (older backend, or a MIC not yet mapped).
     */
    labelField?: 'label' | 'gboLabel';
    /**
     * When true, only show self-serviceable MM Types (CS 11009 §11009.11 whitelist — the backend
     * tags each option with `eligible`). Defaults to false so existing consumers (Liquidity
     * Metrics) keep showing every type. MM Appointments enables it so the filter offers only the
     * types a firm can actually self-serve. `!== false` keeps the list intact if the flag is
     * absent (older backend).
     */
    eligibleOnly?: boolean;
};

export const MMTypesSelect: FunctionComponent<MMTypesSelectProps> = ({
    disabled = false,
    isMulti = false,
    required = false,
    labelField = 'label',
    eligibleOnly = false
}) => {
    const mmTypes = useStore('mmTypes');
    const dispatch = useDispatch();

    useEffect(() => {
        dispatch(getMMTypes());
    }, [dispatch]);

    const visibleOptions = eligibleOnly
        ? mmTypes.options.filter((option) => option.eligible !== false)
        : mmTypes.options;

    const options =
        labelField === 'label'
            ? visibleOptions
            : visibleOptions.map((option) => ({
                  ...option,
                  label: option.gboLabel ?? option.label
              }));

    return (
        <NySelectInput
            disabled={disabled || !mmTypes.options.length}
            isMulti={isMulti}
            name='mmType'
            options={[
                ...(isMulti
                    ? []
                    : [
                          {
                              label: '-',
                              value: ''
                          }
                      ]),
                ...options
            ]}
            required={required}
            showLabelAsValue
        />
    );
};
